import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Tooltip from '@homeaway/react-tooltip';
import Modal from '@homeaway/react-modal';
import {ButtonLink, ButtonPrimary} from '@homeaway/react-buttons';
import NavCalendar from '@homeaway/react-nav-calendar-month';
import propTypeDateString from '../../utils/propTypeDateString';
import './Base.less';

const checkElementType = (props, propName, componentName) => {
    const instance = typeof window !== 'undefined' && window.Element ? window.Element : Object;
    if (props[propName] && !(props[propName] instanceof instance)) {
        return new Error(`Invalid prop '${propName}' supplied to '${componentName}'. Validation failed.`);
    }
    return null;
};

class Base extends Component {
    static propTypes = {
        alertTemplateComponent: PropTypes.func,
        alertTemplateData: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        /** Optional labels object to override default locale-driven labels on calendar */
        calendarLabels: PropTypes.shape({
            /** For locales that need a year label while displaying date e.g. ja-JP*/
            yearLabel: PropTypes.string,
            monthNames: PropTypes.arrayOf(PropTypes.string),
            shortMonthNames: PropTypes.arrayOf(PropTypes.string),
            dayNames: PropTypes.arrayOf(PropTypes.string),
            shortDayNames: PropTypes.arrayOf(PropTypes.string)
        }),
        calendarRefFunc: PropTypes.func,
        centered: PropTypes.bool,
        className: PropTypes.string,
        clearButtonLabel: PropTypes.string,
        closeOnOutsideClick: PropTypes.bool,
        continueButtonLabel: PropTypes.string,
        dayData: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        dayTemplateComponent: PropTypes.func,
        endDate: propTypeDateString,
        inputs: PropTypes.element,
        isDelayed: PropTypes.bool,
        isMobile: PropTypes.bool,
        isOpen: PropTypes.bool,
        /** A moniker or BCP-47 style language tag string. */
        locale: PropTypes.string,
        minDate: PropTypes.instanceOf(Date),
        maxDate: PropTypes.instanceOf(Date),
        /** There are three different mobile behaviors (2 are only for use with the affiliates program), 1) scrolling - switches to a vertical scrolling list. Currently capped at 24 months. 2) single-affiliates - special single month mode for affiliates program (fits within 392px height). 3) double-affiliates - special double month mode for affiliates program (fits within 392px height). */
        mobileBehavior: PropTypes.oneOf(['scrolling', 'single-affiliates', 'double-affiliates']),
        modalTitle: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
        numMonthsToDisplay: PropTypes.number,
        onDatesCleared: PropTypes.func,
        onOpen: PropTypes.func,
        onClose: PropTypes.func,
        onSubmit: PropTypes.func,
        onCancel: PropTypes.func,
        onFinishClose: PropTypes.func,
        onSelectionChanged: PropTypes.func,
        parentNode: checkElementType,
        placement: PropTypes.oneOf(['top', 'left', 'bottom', 'right']),
        scrollNode: checkElementType,
        selectionChangeMode: PropTypes.string,
        selectionType: PropTypes.string,
        showContinueButton: PropTypes.bool,
        startDate: propTypeDateString,
        /** Flag that specifies whether year should be displayed first, useful in locales like ja-JP*/
        displayYearFirst: PropTypes.bool,
        year: PropTypes.number,
        /** Duration of the fade transition when the tooltip closes in milliseconds */
        fadeOutDuration: PropTypes.number
    }

    static defaultProps = {
        centered: true,
        className: '',
        clearButtonLabel: 'Clear dates',
        closeOnOutsideClick: true,
        continueButtonLabel: 'Continue',
        isDelayed: false,
        isMobile: true,
        isOpen: false,
        calendarLabels: {
            yearLabel: ''
        },
        locale: 'en_US',
        modalTitle: null,
        numMonthsToDisplay: 2,
        onFocus: () => {},
        onOpen: () => {},
        onClose: () => {},
        onSubmit: () => {},
        onCancel: () => {},
        onFinishClose: () => {},
        placement: 'bottom',
        selectionChangeMode: 'start',
        selectionType: 'range',
        displayYearFirst: false,
        showContinueButton: false,
        mobileBehavior: 'scrolling'
    }

    static checkElementType = checkElementType

    constructor(props) {
        super(props);

        this.tooltip = null;
        this.modal = null;
    }

    disableClearButton(startDate, endDate, date) {
        return !startDate && !endDate && !date;
    }

    renderClearButton(buttonClasses, disabled, onClick, label) {
        return (
            <ButtonLink type="button" className={buttonClasses} disabled={disabled} aria-hidden={disabled ? 'true' : 'false'} onClick={onClick} onKeyDown={this.handleKeyDown} label={label}/>
        );
    }

    handleKeyDown = (e) => {
        const {isMobile, onKeydownDatesCleared} = this.props;

        if (!isMobile) {
            onKeydownDatesCleared(e, this.tooltip);
        } else {
            onKeydownDatesCleared(e, this.modal);
        }
    }

    handleOnClose = () => {
        this.props.onClose(this.props.onCancel);
    }

    handleOnContinue = () => {
        this.props.onClose(this.props.onSubmit);
    }

    handleTooltipClose = () => {
        this.handleOnContinue();
        this.props.onFinishClose();
    }

    updateFocusable = () => {
        if (this.tooltip !== null && !this.props.isMobile) {
            this.tooltip.queryFocusableElements();
        }
    }

    renderAlertTemplateComponent() {
        const {alertTemplateComponent, alertTemplateData} = this.props;
        const AlertTemplateComponent = alertTemplateComponent;

        if (alertTemplateComponent) {
            return (
                <div className="picker__alert">
                    <AlertTemplateComponent data={alertTemplateData}/>
                </div>
            );
        }

        return null;
    }

    renderCalendar(props) {
        if (props.isDelayed) {
            return false;
        }

        const {selectionType, month, year, minDate, maxDate, numMonthsToDisplay, date, startDate, endDate, selectionChangeMode, onSelectionChanged, isMobile, mobileBehavior, className, clearButtonLabel, continueButtonLabel, locale, alertTemplateComponent, alertTemplateData, calendarLabels, showContinueButton, displayYearFirst, i18n, ...rest} = props; // eslint-disable-line

        const showContinue = showContinueButton && isMobile;
        const clearButtonClasses = classNames('picker__clear', {mobile: isMobile});
        const showAlert = alertTemplateComponent ? true : false;
        const disableClearButton = this.disableClearButton(startDate, endDate, date);

        const navCalendarClasses = classNames('picker__calendar', {
            [selectionType]: true,
            'picker__calendar--mobile-paging-single-affiliates': isMobile && mobileBehavior === 'single-affiliates',
            'picker__calendar--mobile-paging-double-affiliates': isMobile && mobileBehavior === 'double-affiliates'
        });
        return (
            <div ref={props.calendarRefFunc}>
                <NavCalendar
                    className={navCalendarClasses}
                    month={month}
                    year={year}
                    minDate={minDate}
                    maxDate={maxDate}
                    numMonthsToDisplay={numMonthsToDisplay}
                    selectable
                    selectionType={selectionType}
                    selectedDate={date}
                    selectionStart={startDate}
                    selectionEnd={endDate}
                    selectionChangeMode={selectionChangeMode}
                    onSelectionChanged={onSelectionChanged}
                    mobileBehavior={mobileBehavior}
                    scrollToMonth={isMobile && mobileBehavior === 'scrolling'}
                    locale={locale}
                    displayYearFirst={displayYearFirst}
                    labels={calendarLabels}
                    onUpdateFocusable={this.updateFocusable} //eslint-disable-line
                    i18n={i18n}
                    {...rest}
                />
                {showContinue &&
                    <div className="picker__continue">
                        <ButtonPrimary id="picker-continue-button" block label={continueButtonLabel} onClick={this.handleOnContinue}/>
                    </div>
                }
                {!isMobile &&
                    this.renderClearButton(clearButtonClasses, disableClearButton, props.onDatesCleared, clearButtonLabel)
                }
                {showAlert &&
                    this.renderAlertTemplateComponent()
                }
            </div>
        );
    }

    renderModalTitle() {
        const {
            clearButtonLabel,
            onDatesCleared,
            date,
            endDate,
            startDate,
            isMobile,
            modalTitle
        } = this.props;
        const buttonClasses = classNames('btn', 'btn-link', 'picker__clear', {mobile: isMobile});
        const disableClearButton = this.disableClearButton(startDate, endDate, date);
        const containerClasses = classNames({'picker-modal-title-container': modalTitle});

        return (
            <div className={containerClasses}>
                {this.renderClearButton(buttonClasses, disableClearButton, onDatesCleared, clearButtonLabel)}
                {modalTitle &&
                    <div className="picker-modal-title">{modalTitle}</div>
                }
            </div>
        );
    }

    render() {
        const {
            centered,
            className,
            closeOnOutsideClick,
            inputs,
            isMobile,
            isOpen,
            onFocus,
            onOpen,
            parentNode,
            placement,
            scrollNode,
            fadeOutDuration
        } = this.props;

        // Mobile (calendar in modal)
        if (isMobile) {
            return (
                <div className={className}>
                    {inputs}
                    <Modal
                        ref={(c) => {
                            this.modal = c;
                        }}
                        id="picker-modal"
                        className="picker__modal"
                        title={this.renderModalTitle()}
                        isOpen={isOpen}
                        onClose={this.handleOnClose}
                        onModalClose={this.props.onFinishClose}
                        onModalOpen={onOpen}
                        isWorkflow
                    >
                        {this.renderCalendar(this.props)}
                    </Modal>
                </div>
            );
        }

        // Desktop (calendar in popover)
        return (
            <div className={className}>
                <Tooltip
                    ref={(c) => {
                        this.tooltip = c;
                    }}
                    fadeOutDuration={fadeOutDuration}
                    content={this.renderCalendar(this.props)}
                    isOpen={isOpen}
                    onFocus={onFocus}
                    onClose={this.handleTooltipClose}
                    onOpen={onOpen}
                    parentNode={parentNode}
                    placement={placement}
                    portalClassName="picker__popover"
                    scrollNode={scrollNode}
                    tooltipType="popover"
                    trigger="none"
                    wrappingMethod="block"
                    closeOnOutsideClick={closeOnOutsideClick}
                    centered={centered}
                    interactiveContent
                    interactiveContainer
                >
                    {inputs}
                </Tooltip>
            </div>
        );
    }
}

export default Base;
